import { format } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  InputAdornment,
  Skeleton,
} from '@mui/material';
import { fetchPatients } from '../../redux/slices/patientsSlice';
import { RootState } from '../../redux/store';
import { Patient } from '../../api/services/patients';
import { styled } from '@mui/material/styles';
import { Add, Close, ArrowBack } from '@mui/icons-material';

const sortOptions = [
  { value: 'last_name', label: 'Last name' },
  { value: 'first_name', label: 'First name' },
  { value: 'dob', label: 'Date of Birth' },
];

// Custom styled button for purple border/text
const PurpleButton = styled(Button)(() => ({
  color: '#6C63FF',
  borderColor: '#6C63FF',
  '&:hover': {
    backgroundColor: 'rgba(108, 99, 255, 0.04)',
    borderColor: '#6C63FF',
  },
}));

interface PatientSelectorProps {
  mode?: 'page' | 'modal';
  onAddPatient: () => void;
  onSkip: () => void;
  onSelectPatient: (patientId: string) => void;
  selectedPatientId?: string | null;
  showSkip?: boolean;
  onClose?: () => void; // for modal close button
  onBack?: () => void; // for back button in modal
}

export default function PatientSelector({
  mode = 'page',
  onAddPatient,
  onSkip,
  onSelectPatient,
  selectedPatientId,
  showSkip = true,
  onClose,
  onBack,
}: PatientSelectorProps) {
  const dispatch = useDispatch();
  const {
    data: patients,
    loading: isLoading,
    error: reduxError,
  } = useSelector((state: RootState) => state.patients.allPatients);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState('last_name');
  const [internalSelectedPatientId, setInternalSelectedPatientId] = useState<string | null>(
    selectedPatientId || null
  );

  useEffect(() => {
    dispatch(fetchPatients());
  }, [dispatch]);

  useEffect(() => {
    if (reduxError) {
      setError(reduxError);
    }
  }, [reduxError]);

  // Keep internal state in sync with prop
  useEffect(() => {
    setInternalSelectedPatientId(selectedPatientId || null);
  }, [selectedPatientId]);

  let filteredPatients = patients || [];
  if (searchTerm) {
    filteredPatients = filteredPatients.filter(
      (patient: Patient) =>
        patient.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.medical_info?.medicare?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
  filteredPatients = [...filteredPatients].sort((a: Patient, b: Patient) => {
    if (sortBy === 'dob') {
      return new Date(a.dob).getTime() - new Date(b.dob).getTime();
    } else if (sortBy === 'last_name') {
      return a.last_name.localeCompare(b.last_name);
    } else if (sortBy === 'first_name') {
      return a.first_name.localeCompare(b.first_name);
    }
    return 0;
  });

  // Layout differences for modal vs page
  const containerSx =
    mode === 'modal'
      ? { background: 'white', borderRadius: 3, p: 3, minWidth: 400, maxWidth: 600 }
      : { background: 'white', borderRadius: 3, p: 4 };

  return (
    <Box sx={containerSx}>
      <Typography sx={{ fontSize: '1.5rem', mb: 3 }} fontWeight={600}>
        Select patient
      </Typography>
      {onClose && (
        <Button onClick={onClose} sx={{ position: 'absolute', right: 16, top: 16, minWidth: 0 }}>
          <Close />
        </Button>
      )}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <TextField
          placeholder="Search patients"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
          size="small"
        />
        <PurpleButton
          variant="outlined"
          onClick={onAddPatient}
          startIcon={<Add />}
          sx={{ height: 40, minWidth: 160 }}
        >
          Add new patient
        </PurpleButton>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={1}
        sx={{ mb: 1, ml: 0.5 }}
      >
        <TextField
          select
          size="small"
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          label="Sort by"
          sx={{
            width: 220,
            background: '#fff',
          }}
          variant="outlined"
        >
          {sortOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <TableContainer sx={{ borderRadius: 2, border: '1px solid #F0F0F0', mb: 2, maxHeight: 320 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Patient name</TableCell>
              <TableCell>Date of Birth</TableCell>
              <TableCell>Medicare Number</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, idx) => (
                <TableRow key={idx}>
                  <TableCell colSpan={3}>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography color="error">{error}</Typography>
                </TableCell>
              </TableRow>
            ) : filteredPatients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No patients found
                </TableCell>
              </TableRow>
            ) : (
              filteredPatients.map((patient: Patient) => {
                if (!patient.id) return null;
                const patientId = String(patient.id);
                const isSelected = internalSelectedPatientId === patientId;
                const patientName = `${patient.last_name}, ${patient.first_name}`;
                const dob = patient.dob ? format(new Date(patient.dob), 'd MMMM, yyyy') : '';
                let medicare = patient.medical_info?.medicare?.toString() || '';
                medicare = medicare.replace(/(\d{4})(\d{4})(\d{2,})/, '$1 $2 $3');
                return (
                  <TableRow
                    key={patient.id}
                    hover
                    onClick={() => {
                      setInternalSelectedPatientId(patientId);
                      onSelectPatient(patientId);
                    }}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: isSelected ? 'rgba(108,99,255,0.08)' : 'inherit',
                      transition: 'background 0.2s',
                    }}
                  >
                    <TableCell>{patientName}</TableCell>
                    <TableCell>{dob}</TableCell>
                    <TableCell>{medicare}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {mode === 'modal' && (
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={onBack || onClose}
            sx={{ color: '#6C63FF', fontWeight: 500 }}
          >
            Back
          </Button>
        )}
        {showSkip && (
          <PurpleButton variant="outlined" sx={{ minWidth: 100 }} onClick={onSkip}>
            Skip
          </PurpleButton>
        )}
      </Box>
    </Box>
  );
}
